import {useNavigate} from 'react-router-dom';

import {FeaturesEnum, GuidesEnum} from 'presentation/constants';
import {useContractStatus} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useAccountInformation} from 'presentation/pages/private/hooks';
import {useAdvanceDiagnosticsBookingStore} from 'presentation/pages/private/store/useAdvanceDiagnosticsBookingStore';
import {useOpenOptionsDrawerStore} from 'presentation/pages/private/store/useOpenOptionsDrawer';

import {OptionsDrawerItem} from '../types';
import {useAccreditedNetworkItems} from './useAccreditedNetworkItems';
import {useNotredameNetworkItems} from './useNotredameNetworkItems';
import {useTelemedicineItems} from './useTelemedicineItems';
import {usePpoNetworkStore} from 'presentation/modules/guide/shared/store/guideStore/usePpoNetworkStore';
import {ppoNetworkTypeEnum} from 'presentation/modules/guide/shared/store/guideStore/types';
import {translations} from 'presentation/translations-new';
import saludiaLogo from 'presentation/assets/images/saludia-logo.svg';
import {useSaludiaExperience} from 'presentation/hooks/useSaludiaExperience';
import {useSaludiaDrawerStore} from 'presentation/pages/private/store/useSaludiaDrawerStore';
import {SaludiaDrawerType} from 'presentation/pages/private/store/useSaludiaDrawerStore/types';

const {accreditedNetwork, telemedicine} =
	translations['pt-br'].privateFlow.home.portalShortcutLabels;
const {proprietaryNetwork, saludia, advanceDiagnostics} = translations['pt-br'].privateFlow.services;

function useNetworkProviderItems() {
	const navigate = useNavigate();
	const {openOptionsDrawer, closeOptionsDrawer} = useOpenOptionsDrawerStore();
	const setPpoNetworkType = usePpoNetworkStore(state => state.setPpoNetworkType);
	const openSaludiaDrawer = useSaludiaDrawerStore(state => state.openSaludiaDrawer);
	const {isSaludiaEnabled} = useSaludiaExperience();
	const startAdvanceDiagnosticsBooking = useAdvanceDiagnosticsBookingStore(
		state => state.startAdvanceDiagnosticsBooking,
	);
	const {isEnabled: isAdvanceDiagnosticsExamsJourneyEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_PPO_ADVANCE_DIAGNOSTICS_EXAMS_JOURNEY,
	);

	const {items: accreditedNetworkItems} = useAccreditedNetworkItems();
	const {items: notredameNetworkItems} = useNotredameNetworkItems();
	const {items: telemedicineItems} = useTelemedicineItems();

	const {
		flPlanoSomenteHospitalar,
		healthOnly,
		odontoOnly,
		healthOdonto,
		isResponsibleWithoutBenefits,
		isB2BAccess,
		isEarlyAccess,
	} = useAccountInformation();
	const {isValid, isContractSuspended} = useContractStatus();

	const showAccreditedNetwork = isValid && !isB2BAccess && !isResponsibleWithoutBenefits;

	const showNotredameNetwork =
		isValid && !isResponsibleWithoutBenefits && !isB2BAccess && (healthOnly || healthOdonto);

	const showBookService =
		!isResponsibleWithoutBenefits &&
		(healthOdonto || (healthOnly && !flPlanoSomenteHospitalar));

	const showBookTelehealth = isValid && showBookService && !isEarlyAccess;

	const showSaludiaNetwork =
		isSaludiaEnabled && isValid && !isB2BAccess && !isResponsibleWithoutBenefits;
	const showAdvanceDiagnostics =
		isAdvanceDiagnosticsExamsJourneyEnabled &&
		isValid &&
		!isContractSuspended &&
		showBookService &&
		!isEarlyAccess;

	const handleRedeCredenciadaClick = () => {
		if (healthOnly) {
			setPpoNetworkType(ppoNetworkTypeEnum.ACCREDITED_NETWORK);
			closeOptionsDrawer();
			navigate(GuidesEnum.SERVICE_NETWORK_HEALTH);
			return;
		}

		if (odontoOnly) {
			closeOptionsDrawer();
			navigate(GuidesEnum.SERVICE_NETWORK_ODONTO);
			return;
		}

		openOptionsDrawer(accreditedNetwork, accreditedNetworkItems);
	};

	const handleRedeNotredameClick = () => {
		openOptionsDrawer(proprietaryNetwork, notredameNetworkItems);
	};

	const handleAdvanceDiagnosticsClick = () => {
		closeOptionsDrawer();
		startAdvanceDiagnosticsBooking();
	};

	const handleTelemedicineClick = () => {
		openOptionsDrawer(telemedicine, telemedicineItems);
	};

	const handleSaludiaClick = (drawer: SaludiaDrawerType) => () => {
		closeOptionsDrawer();
		openSaludiaDrawer(drawer);
	};

	const ALL_ITEMS: (OptionsDrawerItem & {show: boolean})[] = [
		{
			label: accreditedNetwork,
			icon: 'pin-drop-filled',
			onClick: handleRedeCredenciadaClick,
			show: showAccreditedNetwork,
		},
		{
			label: proprietaryNetwork,
			icon: 'notredame-logo',
			onClick: handleRedeNotredameClick,
			show: showNotredameNetwork,
		},
		{
			label: saludia.network,
			imageSrc: saludiaLogo,
			onClick: handleSaludiaClick('card-code'),
			link: {label: saludia.learnMore, onClick: handleSaludiaClick('info')},
			show: showSaludiaNetwork,
		},
		{
			label: advanceDiagnostics,
			icon: 'syringe-default',
			onClick: handleAdvanceDiagnosticsClick,
			show: showAdvanceDiagnostics,
		},
		{
			label: telemedicine,
			icon: 'video-default',
			onClick: handleTelemedicineClick,
			show: showBookTelehealth,
		},
	];

	const items: OptionsDrawerItem[] = ALL_ITEMS.filter(item => item.show).map(
		({show: _, ...item}) => item,
	);

	return {items};
}

export {useNetworkProviderItems};
